import { useCallback, useEffect, useState } from "react";
import {
  Activity,
  Ban,
  ChevronDown,
  Clock3,
  Eraser,
  LogOut,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

interface AdminSession {
  id: string;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  userAgent: string;
  ipAddress: string;
  online: boolean;
}

interface AdminAccount {
  id: string;
  username: string;
  recoveryEmail: string;
  role: "admin" | "member";
  bannedAt: string | null;
  createdAt: string;
  sessions: AdminSession[];
}

const dateTime = new Intl.DateTimeFormat("zh-CN", {
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

function formatTime(value: string) {
  return dateTime.format(new Date(value));
}

export function AdminWorkspace({
  currentAccountId,
}: {
  currentAccountId?: string;
}) {
  const [accounts, setAccounts] = useState<AdminAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyAction, setBusyAction] = useState("");

  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const result = await api<{ accounts: AdminAccount[] }>(
        "/api/admin/accounts",
      );
      setAccounts(result.accounts);
      setError("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "管理数据加载失败");
    } finally {
      if (initial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(true);
    const timer = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(timer);
  }, [load]);

  const runAction = async (
    account: AdminAccount,
    action: "kick" | "ban" | "unban",
  ) => {
    if (
      action === "ban" &&
      !window.confirm(
        `确定封禁 ${account.username}？该账号会立即退出并无法重新登录。`,
      )
    )
      return;
    setBusyAction(`${account.id}:${action}`);
    try {
      await api<{ ok: true }>(
        `/api/admin/accounts/${encodeURIComponent(account.id)}/${action}`,
        { method: "POST" },
      );
      toast.success(
        action === "kick"
          ? `已 Kick ${account.username}`
          : action === "ban"
            ? `已封禁 ${account.username}`
            : `已解封 ${account.username}`,
      );
      await load();
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "操作失败");
    } finally {
      setBusyAction("");
    }
  };

  const purgeStale = async () => {
    if (
      !window.confirm(
        "删除所有 10 分钟无活动的会话记录？这些账号下次访问需重新登录。",
      )
    )
      return;
    setBusyAction("purge");
    try {
      const result = await api<{ ok: true; purged: number }>(
        "/api/admin/sessions/purge-stale",
        { method: "POST" },
      );
      toast.success(`已清理 ${result.purged} 个已下线会话`);
      await load();
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "清理失败");
    } finally {
      setBusyAction("");
    }
  };

  const onlineUsers = accounts.filter((account) =>
    account.sessions.some((session) => session.online),
  ).length;
  const activeSessions = accounts.reduce(
    (total, account) => total + account.sessions.length,
    0,
  );
  const bannedUsers = accounts.filter((account) => account.bannedAt).length;

  return (
    <>
      <section className="flex items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold">
            <ShieldCheck className="size-5" />
            管理员工作台
          </h1>
          <p className="mt-1 text-xs text-muted-foreground">
            活跃会话每 5 秒自动刷新
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void purgeStale()}
            disabled={Boolean(busyAction) || loading}
            aria-label="清理已下线会话"
            title="删除所有 10 分钟无活动的会话记录"
          >
            <Eraser className={busyAction === "purge" ? "animate-pulse" : ""} />
            清理已下线会话
          </Button>
          <Button
            variant="secondary"
            size="icon"
            onClick={() => void load(true)}
            aria-label="刷新会话"
            title="刷新会话"
            disabled={loading}
          >
            <RefreshCw className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
      </section>

      <section
        aria-label="会话概览"
        className="mt-4 flex flex-wrap gap-x-6 gap-y-2 rounded-lg bg-card px-4 py-3 text-sm"
      >
        <span className="flex items-center gap-2">
          <Users className="size-4 text-muted-foreground" />
          <strong>{onlineUsers}</strong> 个在线账号
        </span>
        <span className="flex items-center gap-2">
          <Activity className="size-4 text-muted-foreground" />
          <strong>{activeSessions}</strong> 个活跃会话
        </span>
        <span className="flex items-center gap-2">
          <Ban className="size-4 text-muted-foreground" />
          <strong>{bannedUsers}</strong> 个已封禁
        </span>
      </section>

      {error && (
        <p
          role="alert"
          className="mt-4 rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      )}

      <section aria-label="账号与会话列表" className="mt-4 space-y-2">
        {loading && accounts.length === 0 ? (
          <div className="rounded-lg bg-card px-4 py-10 text-center text-sm text-muted-foreground">
            正在读取活跃会话…
          </div>
        ) : (
          accounts.map((account) => {
            const lastSeen = account.sessions[0]?.lastSeenAt;
            const isCurrent = account.id === currentAccountId;
            return (
              <article
                key={account.id}
                data-admin-account={account.username}
                className="rounded-lg bg-card p-4"
              >
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-sm font-semibold">
                        {account.username}
                      </h2>
                      {account.role === "admin" && (
                        <Badge variant="secondary">管理员</Badge>
                      )}
                      {account.bannedAt ? (
                        <Badge variant="destructive">已封禁</Badge>
                      ) : account.sessions.some((session) => session.online) ? (
                        <Badge className="bg-emerald-400/10 text-emerald-300">
                          <span className="size-1.5 rounded-full bg-current" />
                          在线
                        </Badge>
                      ) : (
                        <Badge variant="secondary">离线</Badge>
                      )}
                    </div>
                    <p className="mt-1 truncate text-xs text-muted-foreground">
                      {account.recoveryEmail}
                    </p>
                  </div>

                  <div className="flex gap-6 text-xs text-muted-foreground lg:gap-8">
                    <div>
                      <p>活跃会话</p>
                      <p className="mt-1 text-sm font-medium text-foreground">
                        {account.sessions.length}
                      </p>
                    </div>
                    <div>
                      <p>最近活动</p>
                      <p className="mt-1 text-sm font-medium text-foreground">
                        {lastSeen ? formatTime(lastSeen) : "—"}
                      </p>
                    </div>
                  </div>

                  <div className="flex min-h-9 items-center gap-2 lg:justify-end">
                    {isCurrent ? (
                      <span className="text-xs text-muted-foreground">
                        当前账号
                      </span>
                    ) : account.bannedAt ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={Boolean(busyAction)}
                        onClick={() => void runAction(account, "unban")}
                        aria-label={`解封 ${account.username}`}
                      >
                        <RotateCcw />
                        解封
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={
                            Boolean(busyAction) || account.sessions.length === 0
                          }
                          onClick={() => void runAction(account, "kick")}
                          aria-label={`Kick ${account.username}`}
                          title={
                            account.sessions.length === 0
                              ? "没有活跃会话"
                              : "强制该账号所有会话下线"
                          }
                        >
                          <LogOut />
                          Kick
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={Boolean(busyAction)}
                          onClick={() => void runAction(account, "ban")}
                          aria-label={`Ban ${account.username}`}
                        >
                          <Ban />
                          Ban
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {account.sessions.length > 0 && (
                  <details className="group mt-3">
                    <summary className="flex min-h-9 items-center gap-2 text-xs text-muted-foreground hover:text-foreground">
                      <ChevronDown className="size-3.5 transition-transform group-open:rotate-180" />
                      查看 {account.sessions.length} 个活跃会话
                    </summary>
                    <div className="mt-2 space-y-2">
                      {account.sessions.map((session) => (
                        <div
                          key={session.id}
                          className="rounded-md bg-background/60 p-3 text-xs"
                        >
                          <div className="mb-2 flex items-center gap-2">
                            {session.online ? (
                              <Badge className="bg-emerald-400/10 text-emerald-300">
                                <span className="size-1.5 rounded-full bg-current" />
                                在线
                              </Badge>
                            ) : (
                              <Badge variant="secondary">已下线</Badge>
                            )}
                            <span className="truncate text-muted-foreground">
                              {session.userAgent}
                            </span>
                          </div>
                          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                            <div>
                              <p className="text-muted-foreground">登录时间</p>
                              <p className="mt-1">
                                {formatTime(session.createdAt)}
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">最近活动</p>
                              <p className="mt-1">
                                {formatTime(session.lastSeenAt)}
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">到期时间</p>
                              <p className="mt-1">
                                {formatTime(session.expiresAt)}
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">IP</p>
                              <p className="mt-1 break-all">
                                {session.ipAddress}
                              </p>
                            </div>
                          </div>
                          <div className="mt-3 flex items-start gap-2 text-muted-foreground">
                            <Clock3 className="mt-0.5 size-3.5 shrink-0" />
                            <span className="break-all">
                              {session.userAgent}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </article>
            );
          })
        )}
      </section>
    </>
  );
}
